import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { ROUTES } from 'routes/constants';

// Mapeamento das chaves da API para os valores de exibição
const apiKeyToDisplayMap: Record<string, string> = {
  'women_income_sources.prestacao_servico_externo': 'Prestação de Serviço Externo',
  'women_income_sources.assalariado_atividade_fora_propriedade': 'Assalariado em Atividade Fora da Propriedade',
  'women_income_sources.atividade_produtiva_propriedade': 'Atividade Produtiva na Propriedade',
  'women_income_sources.aposentadoria': 'Aposentadoria',
  'women_income_sources.pensao': 'Pensão',
  'women_income_sources.recursos_programas_sociais': 'Recursos de Programas Sociais'
};

export const RendaParticipacaoFemininaVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [dados, setDados] = useState<{
    fontesMulheres: string[],
    outrosAdicionados: string[],
    mulheresDecidem: string
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) {
        setError('UUID não encontrado');
        setLoading(false);
        return;
      }

      try {
        // Primeiro tenta restaurar do localStorage: per-collection, depois global
        try {
          const storageKeys: string[] = [];
          if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
          storageKeys.push('cuffa-storage');

          for (const storageKey of storageKeys) {
            try {
              const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
              if (!raw) continue;
              const parsed = JSON.parse(raw);
              const saved = parsed?.state?.dadosParciais?.participacaoFeminina;
              if (!saved) continue;

              const fontesArr: string[] = Array.isArray(saved.fontesMulheres)
                ? saved.fontesMulheres
                : Array.isArray(saved.rendaMulher)
                  ? saved.rendaMulher
                  : [];

              const outrosArr: string[] = Array.isArray(saved.outrosAdicionados)
                ? saved.outrosAdicionados
                : Array.isArray(saved.outros)
                  ? saved.outros
                  : [];

              const fontesMulheres: string[] = fontesArr.filter((f: any) => typeof f === 'string' && f.trim()).map((s: string) => s.trim());
              const outrosAdicionados: string[] = outrosArr.filter((o: any) => typeof o === 'string' && o.trim()).map((s: string) => s.trim());

              let decisaoVal: any = saved.mulheresDecidem ?? saved.decisaomulheres ?? saved.decisao ?? saved.mulheres_tomada_decisao ?? '';
              if (typeof decisaoVal === 'boolean') decisaoVal = decisaoVal ? 'sim' : 'nao';
              if (decisaoVal === 'NAO_SE_APLICA') decisaoVal = 'nao_se_aplica';

              setDados({
                fontesMulheres,
                outrosAdicionados,
                mulheresDecidem: (decisaoVal || '').toString().toLowerCase()
              });
              setLoading(false);
              return;
            } catch (e) {
              console.warn(`Falha ao parsear ${storageKey} ao restaurar participacaoFeminina:`, e);
              continue;
            }
          }
        } catch (e) {
          console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar participacaoFeminina', e);
        }

        // Se não encontrou no storage, chama API como antes
        const response = await CuffaAPI.getDataByID(uuid);
        const apiData = response?.dados;

        if (!apiData) {
          setError('Dados não encontrados na resposta da API');
          setLoading(false);
          return;
        }

        // Processa as fontes de renda da API
        const fontesMulheres: string[] = [];
        const outrosAdicionados: string[] = [];

        Object.entries(apiKeyToDisplayMap).forEach(([apiKey, displayValue]) => {
          const keys = apiKey.split('.');
          let value: any = apiData;
          for (const key of keys) {
            value = value?.[key];
            if (value === undefined) break;
          }
          if (value === true) {
            fontesMulheres.push(displayValue);
          }
        });

        if (apiData.women_income_sources?.outras && Array.isArray(apiData.women_income_sources.outras)) {
          apiData.women_income_sources.outras.forEach((outro: { descricao: string }) => {
            if (outro.descricao) {
              const descricaoFormatada = outro.descricao.trim();
              if (descricaoFormatada) {
                outrosAdicionados.push(descricaoFormatada);
                fontesMulheres.push(descricaoFormatada);
              }
            }
          });
        }

        if (apiData.women_income_sources?.nao_se_aplica === true) {
          fontesMulheres.push('NÃO SE APLICA');
        }

        let decisao = apiData.women_income_sources?.mulheres_tomada_decisao ||
          apiData.women_decision_participation || '';
        if (decisao === 'NAO_SE_APLICA') {
          decisao = 'nao_se_aplica';
        }

        setDados({
          fontesMulheres,
          outrosAdicionados,
          mulheresDecidem: decisao.toLowerCase()
        });

      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setError('Erro ao carregar os dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid]);

  // Formata a resposta sobre tomada de decisão
  const formatarDecisao = (valor: string) => {
    if (!valor) return 'Não informado';

    switch (valor.toLowerCase()) {
      case 'sim':
        return 'Sim, as mulheres participam';
      case 'nao':
        return 'Não, as mulheres não participam';
      case 'nao_se_aplica':
        return 'Não se aplica';
      default:
        return valor;
    }
  };

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Renda e Participação Feminina" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (error) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Renda e Participação Feminina" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Alert severity="error">{error}</Alert>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Renda e Participação Feminina" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Alert severity="info">Nenhum dado encontrado</Alert>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização da Renda e Participação Feminina" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Dados de Renda e Participação Feminina" width="100%">
            {/* Seção: Fontes de renda exercidas por mulheres */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Fontes de Renda Exercidas por Mulheres
              </Typography>

              {dados.fontesMulheres.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {dados.fontesMulheres.map((fonte, index) => (
                    <Chip
                      key={index}
                      label={fonte}
                      color={fonte === 'NÃO SE APLICA' ? 'default' : 'primary'}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">Nenhuma fonte de renda selecionada</Alert>
              )}

              {dados.outrosAdicionados.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Outras fontes adicionadas:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {dados.outrosAdicionados.map((outro, index) => (
                      <Chip
                        key={`outro-${index}`}
                        label={outro}
                        color="secondary"
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>

            {/* Seção: Participação nas decisões */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Participação nas Tomadas de Decisão
              </Typography>
              <Typography>
                {formatarDecisao(dados.mulheresDecidem)}
              </Typography>
            </Paper>

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.FONTES_RENDA_FAMILIA_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.DIFICULDADES_PRODUCAO_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default RendaParticipacaoFemininaVisualizar;
